import React, { useState, useCallback, useRef, useEffect } from "react";
import { Search, X, Loader2, ArrowRight } from "lucide-react";
import { useLanguage } from "../../../contexts/LanguageContext";
import { useNavigate } from "react-router-dom";
import "./SearchBar.css";

export default function SearchBar({
  value = "",
  onChange,
  onSubmit,
  placeholder,
  autoFocus = false,
  results = [],
  onSelectResult,
  isLoading = false,
  enableDropdown = true,
}) {
  const { lang, t } = useLanguage();
  const isAr = lang === "ar";
  const navigate = useNavigate();

  const [localValue, setLocalValue] = useState(value);
  const [isOpen, setIsOpen] = useState(false);
  const wrapperRef = useRef(null);

  useEffect(() => {
    setLocalValue(value);
  }, [value]);

  useEffect(() => {
    function handleClickOutside(e) {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleChange = useCallback(
    (e) => {
      const val = e.target.value;
      setLocalValue(val);
      setIsOpen(true);
      onChange?.(val);
    },
    [onChange]
  );

  const handleClear = useCallback(() => {
    setLocalValue("");
    setIsOpen(false);
    onChange?.("");
  }, [onChange]);

  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === "Escape") {
        handleClear();
      }
      if (e.key === "Enter" && localValue.trim()) {
        setIsOpen(false);
        if (onSubmit) {
          onSubmit(localValue.trim());
        } else {
          navigate(`/search?q=${encodeURIComponent(localValue.trim())}`);
        }
      }
    },
    [handleClear, localValue, navigate, onSubmit]
  );

  const handleSelect = (item) => {
    setIsOpen(false);
    if (onSelectResult) {
      onSelectResult(item);
    } else if (item.id) {
      navigate(`/product/${item.id}`);
    }
  };

  const defaultPlaceholder =
    placeholder ??
    t("search_placeholder", isAr ? "ابحث في المنتجات..." : "Search products...");

  const showDropdown =
    isOpen &&
    (isLoading || results.length > 0 || (localValue && !isLoading && results.length === 0));

  return (
    <div className="searchbar-wrapper" dir={isAr ? "rtl" : "ltr"} ref={wrapperRef}>
      <div className="searchbar-box">
        <Search size={18} className="searchbar-icon" aria-hidden="true" />
        <input
          type="text"
          inputMode="search"
          enterKeyHint="search"
          className="searchbar-input"
          value={localValue}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onFocus={() => localValue && setIsOpen(true)}
          placeholder={defaultPlaceholder}
          autoFocus={autoFocus}
          aria-label={isAr ? "البحث في المنتجات" : "Search products"}
          role="searchbox"
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="off"
          spellCheck={false}
        />
        {localValue && (
          <button
            type="button"
            className="searchbar-clear"
            onClick={handleClear}
            aria-label={isAr ? "مسح البحث" : "Clear search"}
          >
            <X size={16} />
          </button>
        )}
      </div>

      {enableDropdown && showDropdown && (
        <div className="search-dropdown searchbar-dropdown" dir={isAr ? "rtl" : "ltr"}>
          {isLoading && (
            <div className="search-dropdown-loading">
              <Loader2 size={18} className="spin" />
              <span>{isAr ? "جاري البحث..." : "Searching..."}</span>
            </div>
          )}

          {!isLoading && results.length === 0 && localValue && (
            <div className="search-dropdown-empty">
              {isAr ? "لا توجد نتائج" : "No results found"}
            </div>
          )}

          {!isLoading && results.length > 0 && (
            <ul className="search-dropdown-list">
              {results.map((item, index) => (
                <li
                  key={item.id ?? index}
                  className="search-dropdown-item"
                  onClick={() => handleSelect(item)}
                  role="option"
                >
                  <Search size={16} className="search-dropdown-item-icon" />
                  <span className="search-dropdown-item-text">
                    {isAr
                      ? item.name_ar || item.name_en
                      : item.name_en || item.name_ar}
                  </span>
                  {item.category_name && (
                    <span className="search-dropdown-item-category">
                      {item.category_name}
                    </span>
                  )}
                  <ArrowRight size={16} className="search-dropdown-arrow" />
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}